import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { StructuralData } from '@df/core/meta/structural-data/structural-data.namespace';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';

/**
 * Site-wide Trustpilot identifiers used to fetch the product review JSON-LD
 * server-side. `businessUnitId` mirrors the value configured on the Trustpilot
 * CMS widget; `jsonLdTemplateId` is Trustpilot's fixed product-review JSON-LD
 * template id. Both were captured from the live Trustpilot widget request on
 * 2026-06-27 (widget.trustpilot.com/data/jsonld/business-unit/.../product).
 */
export interface ITrustpilotStructuralDataConfig {
    businessUnitId: string;
    /** Trustpilot's product-review JSON-LD template id (constant per Trustpilot, not the display widget template). */
    jsonLdTemplateId: string;
    reviewLanguages: string;
    numberOfReviews: number;
}

export const TRUSTPILOT_STRUCTURAL_DATA_CONFIG = new InjectionToken<ITrustpilotStructuralDataConfig>(
    'TRUSTPILOT_STRUCTURAL_DATA_CONFIG'
);

/** Upper bound (ms) for the server-side Trustpilot fetch so a slow/unavailable Trustpilot never stalls SSR. */
const TRUSTPILOT_FETCH_TIMEOUT = 2500;

export interface ITrustpilotProductReviews {
    review?: StructuralData.IReviewData[];
    aggregateRating?: StructuralData.IAggregateRatingData;
}

/**
 * Builds a stable dedupe key for a review from its author, body and rating.
 * Trustpilot occasionally returns the same review several times with only the
 * `datePublished` timestamp differing by a few seconds (e.g. "Peter Brown" x3),
 * so the timestamp is deliberately excluded from the key.
 */
function reviewKey(review: StructuralData.IReviewData): string {
    const author = review.author && typeof review.author === 'object' ? review.author.name : review.author;
    const rating = review.reviewRating?.ratingValue;
    return [author ?? '', (review.reviewBody ?? '').trim(), rating ?? ''].join('|');
}

/**
 * Removes duplicate reviews (same author + body + rating), preserving order.
 * Accepts the singular-or-array shape schema.org permits.
 */
export function dedupeReviews(
    review: StructuralData.IReviewData | StructuralData.IReviewData[] | undefined
): StructuralData.IReviewData[] {
    if (!review) {
        return [];
    }
    const list = Array.isArray(review) ? review : [review];
    const seen = new Set<string>();
    const out: StructuralData.IReviewData[] = [];
    for (const r of list) {
        if (!r) {
            continue;
        }
        const key = reviewKey(r);
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        out.push(r);
    }
    return out;
}

/**
 * Fetches Trustpilot's product review JSON-LD so reviews + aggregateRating can
 * be merged into our Product schema during server-side rendering, instead of
 * only appearing after the client-side Trustpilot widget runs.
 *
 * The HTTP request is issued only on the SERVER, where the app runs zoneful
 * (provideZonelessChangeDetection() is applied on the browser only). A
 * withFetch() HttpClient request therefore registers as an Angular pending task
 * and keeps the SSR render open until it resolves, so the merged result is
 * serialised into the rendered HTML. The result is stored in TransferState so the client
 * reuses it without a second request (keeping SSR and hydrated DOM consistent).
 * On the client with no transferred state we return null and let the live
 * Trustpilot widget + TrustpilotSchemaObserverService populate the schema as
 * before -- so SPA navigations and a missing config degrade gracefully.
 */
@Injectable({ providedIn: 'root' })
export class TrustpilotReviewService {
    private readonly http = inject(HttpClient);
    private readonly transferState = inject(TransferState);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly config = inject(TRUSTPILOT_STRUCTURAL_DATA_CONFIG, { optional: true });

    fetchProductReviews(opts: { skus: string; productName: string; url: string }): Promise<ITrustpilotProductReviews | null> {
        if (!this.config?.businessUnitId || !opts.skus) {
            return Promise.resolve(null);
        }

        const stateKey = makeStateKey<ITrustpilotProductReviews | null>(
            `tp-reviews-${this.config.businessUnitId}-${opts.skus}`
        );

        const cached = this.transferState.get(stateKey, null);
        if (cached) {
            return Promise.resolve(cached);
        }

        // Only fetch on the server; the client relies on the transferred state
        // (above) or the existing client-side widget/observer path.
        if (!isPlatformServer(this.platformId)) {
            return Promise.resolve(null);
        }

        const requestUrl = this.buildUrl(opts);
        return firstValueFrom(this.http.get<unknown>(requestUrl).pipe(timeout(TRUSTPILOT_FETCH_TIMEOUT)))
            .then(body => {
                const result = this.parse(body);
                this.transferState.set(stateKey, result);
                return result;
            })
            .catch(() => null);
    }

    private buildUrl(opts: { skus: string; productName: string; url: string }): string {
        const cfg = this.config!;
        const params = new URLSearchParams({
            sku: opts.skus,
            numberOfReviews: String(cfg.numberOfReviews),
            productName: opts.productName,
            reviewLanguages: cfg.reviewLanguages,
            templateId: cfg.jsonLdTemplateId,
            url: opts.url
        });
        return `https://widget.trustpilot.com/data/jsonld/business-unit/${cfg.businessUnitId}/product?${params.toString()}`;
    }

    private parse(body: unknown): ITrustpilotProductReviews | null {
        const product = this.findProductNode(body);
        if (!product) {
            return null;
        }
        const aggregateRating = product['aggregateRating'] as StructuralData.IAggregateRatingData | undefined;
        const review = dedupeReviews(
            product['review'] as StructuralData.IReviewData | StructuralData.IReviewData[] | undefined
        );
        if (!aggregateRating && !review.length) {
            return null;
        }
        const result: ITrustpilotProductReviews = {};
        if (aggregateRating) {
            result.aggregateRating = aggregateRating;
        }
        if (review.length) {
            result.review = review;
        }
        return result;
    }

    /** Finds the first Product/ProductGroup node anywhere in the parsed JSON-LD. */
    private findProductNode(value: unknown): Record<string, unknown> | undefined {
        const stack: unknown[] = [value];
        while (stack.length) {
            const cur = stack.pop();
            if (!cur || typeof cur !== 'object') {
                continue;
            }
            if (Array.isArray(cur)) {
                stack.push(...cur);
                continue;
            }
            const node = cur as Record<string, unknown>;
            if (this.isProductType(node['@type'])) {
                return node;
            }
            if (node['@graph']) {
                stack.push(node['@graph']);
            }
        }
        return undefined;
    }

    private isProductType(type: unknown): boolean {
        if (Array.isArray(type)) {
            return type.some(item => this.isProductType(item));
        }
        return type === StructuralData.Type.product || type === StructuralData.Type.productGroup;
    }
}
